import type { ActionButtonColor } from '../Input2/ActionButton';
import type { ColorName } from '../Input2/colors';
import { cn } from '../cn';

/**
 * Voice-mode palette — one source of truth for every "Voice Drop" / voice-note
 * surface (dashboard Todo, DFY contact page, the shared VoiceNoteActionCardPure
 * the Chrome extension renders).
 *
 * Built on the deep brand navy (`zumthor` 800–950, the left-nav active state).
 * Voice mode is told apart from Direct Message (astronaut / blue) by a quiet
 * navy tint + border and the mic icon — not by a loud hue. Recording keeps red.
 */

/** Input2 color for voice-mode buttons, textareas and selects. */
export const VOICE_COLOR = 'navy' satisfies ColorName;

/** ActionButton color for voice-mode primaries (generate / must-listen play). */
export const VOICE_ACTION_COLOR = 'navy' satisfies ActionButtonColor;

/** Composer container (textarea + clip picker) in voice mode. */
export const voiceContainerClassName =
  'border-zumthor-300 bg-zumthor-50/60 dark:border-zumthor-800 dark:bg-zumthor-950/40';

/** Collapsed / clickable voice container — adds the hover state on top of the tint. */
export const voiceContainerHoverClassName =
  'hover:border-zumthor-400 hover:bg-zumthor-50 dark:hover:border-zumthor-700 dark:hover:bg-zumthor-950/70';

/** "+" divider between the voice script and the clip picker. */
export const voiceSeparatorIconClassName = 'text-zumthor-700 bg-zumthor-100 dark:text-zumthor-200 dark:bg-zumthor-900';
export const voiceSeparatorBarClassName = 'bg-zumthor-200 dark:bg-zumthor-800';

/** Muted voice accent — placeholder glyphs, empty-state copy. */
export const voiceMutedTextClassName = 'text-zumthor-500 dark:text-zumthor-400';

/** Emphasized voice status copy ("Listen to audio before sending"). */
export const voiceStatusTextClassName = 'text-zumthor-800 dark:text-zumthor-300 font-semibold';

/** Round play/pause chip on each voice clip in the clip picker. */
export function voiceClipPlayClassName(isPlaying: boolean) {
  return cn(
    'size-7 rounded-full transition-colors',
    isPlaying
      ? 'bg-zumthor-800 text-white hover:bg-zumthor-900 dark:bg-zumthor-700 dark:hover:bg-zumthor-800'
      : 'bg-zumthor-100 text-zumthor-800 hover:bg-zumthor-200 dark:bg-zumthor-900/60 dark:text-zumthor-200 dark:hover:bg-zumthor-800',
  );
}
